import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { StorageIntegrityService } from 'app/shared/security';
import { UserService } from 'app/shared/services/user.service';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { LocalStorageService } from '../../shared/services/local-storage.service';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const storageService = inject(LocalStorageService);
    const storageIntegrity = inject(StorageIntegrityService);
    const userService = inject(UserService);

    const prepareRequest = async (): Promise<HttpRequest<unknown>> => {
        let secureToken = '';

        try {
            secureToken = (await storageIntegrity.getSecureData('accessToken')) ?? '';
        } catch (error) {
            secureToken = '';
        }

        const normalizedToken = (secureToken || storageService.accessToken || '').trim();
        const tokenForValidation = normalizedToken.replace(/^Bearer\s+/i, '');

        const headersToSet: Record<string, string> = {};

        const shouldAttachAuthHeader =
            !!tokenForValidation && !AuthUtils.isTokenExpired(tokenForValidation);

        if (shouldAttachAuthHeader) {
            headersToSet['Authorization'] = tokenForValidation.startsWith('Bearer ')
                ? tokenForValidation
                : `Bearer ${tokenForValidation}`;
        }

        const resolveUserData = async (): Promise<Record<string, any> | null> => {
            try {
                const secureUser = await storageIntegrity.getSecureData('user');
                if (secureUser) {
                    if (typeof secureUser === 'string') {
                        const parsed = JSON.parse(secureUser);
                        if (parsed && typeof parsed === 'object') {
                            return parsed as Record<string, any>;
                        }
                    }

                    if (typeof secureUser === 'object') {
                        return secureUser as Record<string, any>;
                    }
                }
            } catch (error) {
                // Ignore parsing errors and fallback to other sources.
            }

            if (userService.user) {
                const user = userService.user as unknown;
                if (typeof user === 'object' && user !== null) {
                    return user as Record<string, any>;
                }
            }

            const legacyUser = storageService.user;
            if (legacyUser) {
                if (typeof legacyUser === 'string') {
                    try {
                        const parsed = JSON.parse(legacyUser);
                        if (parsed && typeof parsed === 'object') {
                            return parsed as Record<string, any>;
                        }
                    } catch (error) {
                        return null;
                    }
                }

                if (typeof legacyUser === 'object') {
                    return legacyUser as Record<string, any>;
                }
            }

            return null;
        };

        const userData = await resolveUserData();
        let securitySignature: string | null = null;

        if (userData && typeof userData.security_signature === 'string') {
            securitySignature = userData.security_signature;
        }

        if (!securitySignature && userData && shouldAttachAuthHeader) {
            const userId = userData.user_id ?? userData.id;
            const email = userData.email;

            if (userId && email) {
                try {
                    const signatureResult = await storageIntegrity.generateSecuritySignature(
                        userId,
                        email,
                        tokenForValidation
                    );

                    securitySignature = signatureResult.signature;
                } catch (error) {
                    securitySignature = null;
                }
            }
        }

        if (securitySignature && !req.headers.has('X-Security-Signature')) {
            headersToSet['X-Security-Signature'] = securitySignature;
        }

        if (Object.keys(headersToSet).length > 0) {
            return req.clone({ setHeaders: headersToSet });
        }

        return req;
    };

    return from(prepareRequest()).pipe(
        switchMap((preparedReq) =>
            next(preparedReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                
                console.error('401');
                // Sign out
                authService.signOut();

                // Reload the app
                location.reload();
            }

            return throwError(error);
        })
        ))
    );
};
